import _ from 'lodash';
import * as members from 'privates/privateMembers';
import * as postMessage from 'privates/postMessage';
import Link from 'ui/Link';
import platformUtils from 'platformUtils';
import * as urlUtils from 'privates/urlUtils';
import {validate} from 'privates/validations/validation';
import {generateError, errors} from 'privates/validations/errors';
import monitoring from 'privates/monitoring';
import reporter from 'privates/reporter';
import {setStageUnfriendlyFunctions} from 'privates/utils/livePreview';

/**
 * @namespace wix-location
 * @summary The wix-location module contains functionality for getting information
 *  about the URL of the current page and for navigating to other pages.
 * @description
 *  To use the location module, import wixLocation from the wix-location module:
 *
 *    ```javascript
 *    import wixLocation from 'wix-location';
 *    ```
 *
 *  The APIs in `wix-location` can only be used in front-end code and only after
 *  the page is ready.
 *
 * #### Premium Sites
 * Given the premium site URL: `https://domain.com/animals/mammals/elephant?species=african-elephant`,
 * the URL is broken down as shown in the table below:
 *
 * <table style="border-collapse: collapse;">
 *     <tr>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" colspan=9>url</td>
 *     </tr>
 *     <tr>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" colspan=3>baseUrl</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: #f7f9fa" colspan=6></td>
 *     </tr>
 *     <tr>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" >protocol</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: #f7f9fa" ></td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" >host</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" >prefix</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" >path</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: #f7f9fa" ></td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" >query</td>
 *     </tr>
 *     <tr>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">https</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">://</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">domain.com</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">/animals</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">/mammals/elephant</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">?</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">species=african-elephant</td>
 *     </tr>
 * </table>
 *
 * #### Free Site
 * Given the free site URL: `https://user.wix-sites.com/zoo/animals/mammals/elephant?species=african-elephant`,
 * the URL is broken down as shown in the table below:
 *
 * <table style="border-collapse: collapse;">
 *     <tr>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" colspan=11>url</td>
 *     </tr>
 *     <tr>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white" colspan=5>baseUrl</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: #f7f9fa" colspan=6></td>
 *     </tr>
 *     <tr>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">protocol</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: #f7f9fa"></td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">host</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: #f7f9fa"></td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">site</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">prefix</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">path</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: #f7f9fa"></td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">query</td>
 *     </tr>
 *     <tr>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">https</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">://</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">user.wix-sites.com</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">/</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">zoo</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">/animals</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">/mammals/elephant</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">?</td>
 *         <td style="border: 1px solid #dce0e2; padding: 5px; background-color: white">species=african-elephant</td>
 *     </tr>
 * </table>
 *
 *
 */
class Location {
    constructor(appDefId){
        members._appDefId.set(this, appDefId);
        _.bindAll(this, ['to']);
    }

    /**
     * @member url
     * @label property
     * @syntax
     * get url(): string
     * @summary Gets the full URL of the current page.
     * @description
     *  Premium sites:
     *  ![Premium site URL](images/url_premium_url.png "Premium site URL")
     *
     *  Free sites:
     *  ![Free site URL](images/url_free_url.png "Free site URL")
     *
     *  If the site is not yet published, the function returns null.
     * @type {external:String}
     * @snippet [Location-url.es6=Get the full URL of the current page]
     * @see [baseUrl](#baseUrl)
     * @memberof wix-location
     * @readonly
     */
    get url() {
        const RGI = platformUtils.getRGI();
        return urlUtils.getUrlFromRGI(RGI);
    }

    /*
     ******* COMMENTED OUT — SEE https://jira.wixpress.com/browse/WCD-3608  ******
     * @member hash
     * @label property
     * @syntax
     * get hash(): string
     * @summary Gets the part of the page's URL after the hash (`#`).
     * @description
     *  Premium sites:
     *  ![Premium site hash](images/url_premium_hash.png "Premium site hash")
     *
     *  Free sites:
     *  ![Free site hash](images/url_free_hash.png "Free site hash")
     * @snippet [Location-hash.es6=Get the hash of the current page's URL]
     * @type {external:String}
     * @memberof wix-location
     * @readonly
     */
    get hash() {
        return urlUtils.parseUrl(this.url).hash;
    }

    /**
     * @member protocol
     * @label property
     * @syntax
     * get protocol(): string
     * @summary Gets the protocol of the page's URL.
     * @description
     *  Premium sites:
     *  ![Premium site protocol](images/url_premium_protocol.png "Premium site protocol")
     *
     *  Free sites:
     *  ![Free site protocol](images/url_free_protocol.png "Free site protocol")
     * @snippet [Location-protocol.es6=Get the protocol of the current page's URL]
     * @type {external:String}
     * @memberof wix-location
     * @readonly
     */
    get protocol() {
        return urlUtils.parseUrl(this.url).protocol;
    }

    /**
     * @member prefix
     * @memberof wix-location
     * @label property
     * @syntax
     * get prefix(): string
     * @summary Gets the prefix of a dynamic page's or router page's URL.
     * @description
     *  Only dynamic pages and router pages have a prefix. The value of the
     *  `prefix` property for other page types is always `undefined`.
     *
     *  Premium sites:
     *  ![Premium site prefix](images/url_premium_prefix.png "Premium site prefix")
     *
     *  Free sites:
     *  ![Free site prefix](images/url_free_prefix.png "Free site prefix")
     *
     *  To learn more about dynamic page prefixes, see [About URL Prefixes and Page Grouping of Dynamic Pages](https://support.wix.com/en/article/about-url-prefixes-and-page-grouping-of-dynamic-pages).
     *
     *  To learn more about router page prefixes, see [About Routers](https://support.wix.com/en/article/about-routers#url-prefix).
     * @snippet [Location-prefix.es6=Get the prefix of the current page's URL]
     * @type {external:String}
     * @memberof wix-location
     * @readonly
     */
    get prefix() {
        const RGI = platformUtils.getRGI();
        const nav = RGI.getNavigationInfo();
        return nav && nav.routerDefinition ? nav.routerDefinition.prefix : undefined;
    }

    /**
     * @member path
     * @label property
     * @syntax
     * get path(): string[]
     * @summary Gets the path of the page's URL.
     * @description
     *  Premium sites:
     *  ![Premium site path](images/url_premium_path.png "Premium site path")
     *
     *  Free sites:
     *  ![Free site path](images/url_free_path.png "Free site path")
     * @snippet [Location-path.es6=Get the path of the current page's URL]
     * @type {external:String[]}
     * @memberof wix-location
     * @readonly
     */
    get path() {
        const prefix = this.prefix;
        const parsedUrl = urlUtils.parseUrl(this.url);
        const path = parsedUrl.path;
        const isFreeSite = this.baseUrl.indexOf('wixsite.com') > 0;
        const pathAsArray = path.substring(1).split('/');
        const fullPath = isFreeSite ? pathAsArray.slice(1) : pathAsArray;

        return prefix && fullPath[0] === prefix ? fullPath.slice(1) : fullPath;
    }

    /**
     * @member query
     * @label property
     * @syntax
     * get query(): Object
     * @summary Gets an object that represents the query segment of the page's URL.
     * @description
     *  Premium sites:
     *  ![Premium site query](images/url_premium_query.png "Premium site query")
     *
     *  Free sites:
     *  ![Free site query](images/url_free_query.png "Free site query")
     * @snippet [Location-query.es6=Get the query of the current page's URL]
     * @type {external:Object}
     * @memberof wix-location
     * @readonly
     */
    get query() {
        return urlUtils.parseUrl(this.url).query;
    }

    /**
     * @member baseUrl
     * @label property
     * @syntax
     * get baseUrl(): string
     * @summary Gets the base URL of the page.
     * @description
     *  Premium sites:
     *  ![Premium site baseUrl](images/url_premium_baseUrl.png "Premium site baseUrl")
     *
     *  Free sites:
     *  ![Free site baseUrl](images/url_free_baseUrl.png "Free site baseUrl")
     *
     *  If the site is not yet published, the function returns null.
     * @snippet [Location-baseUrl.es6=Get the base URL of the current page]
     * @type {external:String}
     * @see [url](#url)
     * @memberof wix-location
     * @readonly
     */
    get baseUrl() {
        const RGI = platformUtils.getRGI();
        return RGI.getNavigationInfo().baseUrl;
    }

    /**
     * @function to
     * @syntax
     * function to(url: string): void
     * @summary Directs the browser to navigate to the specified URL.
     * @description
     *  The `to()` function navigates the browser to another web page.
     *
     * `to()` supports the following link patterns:
     *
     *  + `/localPageURL`: another page on your site
     *  + `/localPageURL?queryParam=value`: another page on your site with query parameters
     *  + `/`: your site's home page
     *  + `http(s)://<url>`: an external web address
     *  + `wix:document://<location>`: a document stored in the Media Manager
     *  + `mailto:<address>@<someplace.com>?subject=<subject>`: an email
     *  + `tel:<phone number>`: a phone number
     *
     *
     *  To find the local URL of a page on your site in the Editor:
     *
     *  + Regular page: See the **SEO** tab of the **Page Settings** panel.
     *  + Dynamic page: See the **Page Info** tab of the **Page Settings** panel
     *    for the URL structure. The actual URL used for navigation needs to contain
     *    values where the placeholders are.
     *
     *    For example, if the URL structure of your dynamic page looks like:
     *
     *    ![Dynamic Page URL](images/dynamic_url.png "Dynamic Page URL")
     *
     *    and you have an item with the title "Waffles", the local URL to that page
     *    is /Recipes/Waffles.
     *  + Router page: You cannot navigate directly to a specific router page. You
     *    can navigate to a URL with the router's prefix and the router code
     *    decides which page to route to.
     *
     *
     *  The `to()` function attempts to properly encode the URL parameter that
     *  is passed to it. For example, `.../some page` is encoded to
     *  `.../some%20page`. However, some URLs do not have one unambiguous encoding.
     *  In those cases it is up to you to encode the URL to reflect your intentions.
     *  Because of these situations, it is a best practice to always encode URLs
     *  before you pass them to the `to()` function.
     *
     *  Note that Wix URLs do not contain spaces. A page which has spaces in its
     *  name has its spaces replaced with dashes (`-`). Similarly, a dynamic page
     *  whose URL contains the value of a field in your collection with spaces
     *  has its spaces replaced with dashes (`-`).
     * @param {external:String} url The URL of the page or website to navigate to.
     * @snippet [Location-to_set_local.es6=Navigate to a local link]
     * @snippet [Location-to_set_weblink.es6=Navigate to an external web link]
     * @snippet [Location-to_set_mailto.es6=Open a new email window]
     * @snippet [Location-to_set_document.es6=Open a document]
     * @snippet [Location-to_set_telephone.es6=Make a phone call]
     * @memberof wix-location
     */
    to (url) {
        let linkData;
        if (!validate({compType: 'Location', functionName: 'to', dataToValidate: {url}})) {
            return this;
        }
        try {
            linkData = new Link(url).getData();
        } catch (e) {
            if(e.message === errors.ERROR_API_BEFORE_PAGE_READY){
                throw new monitoring.MonitoringErrorToIgnore(generateError.error_api_with_name_before_page_ready({apiName: 'wix-location.to'}));
            }
            const message = e.message === 'Unsupported link type' ? `${url} is an unsupported url` : e.message;
            reporter.reportSdkError(message);
            return this;
        }

        if (linkData.hasOwnProperty('pageId')) {
            linkData.pageId = linkData.pageId === '#' ? linkData.pageId : linkData.pageId.replace('#', '');
        }

        if (linkData.hasOwnProperty('target')) {
            delete linkData.target;
        }

        postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
            command: postMessage.MessageTypes.NAVIGATE_TO,
            data: linkData,
            appDefinitionId: members._appDefId.get(this)
        });
    }
}
setStageUnfriendlyFunctions(Location, 'to');

export default Location;



// WEBPACK FOOTER //
// ./js/modules/namespaces/location.es6